import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { ArrowDownUp, Info, RefreshCw, Loader2, TrendingUp, DollarSign, Coins, ChevronDown, Check, AlertCircle, Wallet } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fiat currencies with XE-style rates
const FIAT_CURRENCIES = [
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc', flag: '🇨🇭' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵' },
];

// Crypto currencies
const CRYPTO_CURRENCIES = [
  { code: 'BTC', symbol: '₿', name: 'Bitcoin', icon: '🪙', color: 'from-orange-500 to-yellow-500' },
  { code: 'ETH', symbol: 'Ξ', name: 'Ethereum', icon: '💎', color: 'from-blue-500 to-indigo-500' },
  { code: 'USDT', symbol: '$', name: 'Tether', icon: '💵', color: 'from-green-500 to-emerald-500' },
  { code: 'USDC', symbol: '$', name: 'USD Coin', icon: '🔵', color: 'from-blue-400 to-blue-600' },
  { code: 'SOL', symbol: '◎', name: 'Solana', icon: '☀️', color: 'from-purple-500 to-pink-500' },
  { code: 'BNB', symbol: 'BNB', name: 'BNB', icon: '⬡', color: 'from-yellow-400 to-orange-400' },
  { code: 'XRP', symbol: 'XRP', name: 'Ripple', icon: '💧', color: 'from-gray-500 to-gray-700' },
  { code: 'MATIC', symbol: 'MATIC', name: 'Polygon', icon: '🟣', color: 'from-purple-600 to-violet-600' },
];

// Mock XE rates (would come from API in production)
const XE_RATES = {
  EUR: { USD: 1.08, GBP: 0.85, CHF: 0.95, CAD: 1.47, AUD: 1.65, JPY: 162.50 },
  USD: { EUR: 0.93, GBP: 0.79, CHF: 0.88, CAD: 1.36, AUD: 1.53, JPY: 150.25 },
  GBP: { EUR: 1.18, USD: 1.27, CHF: 1.12, CAD: 1.73, AUD: 1.94, JPY: 190.50 },
};

// Mock crypto prices in USD
const CRYPTO_PRICES_USD = {
  BTC: 67500,
  ETH: 3650,
  USDT: 1.00,
  USDC: 1.00,
  SOL: 145,
  BNB: 580,
  XRP: 0.52,
  MATIC: 0.58,
};

const Swap = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  
  // Swap mode: 'fiat-crypto' or 'crypto-crypto'
  const [swapMode, setSwapMode] = useState('fiat-crypto');
  
  // From/To selections
  const [fromCurrency, setFromCurrency] = useState(FIAT_CURRENCIES[2]); // USD
  const [toCurrency, setToCurrency] = useState(CRYPTO_CURRENCIES[0]); // BTC
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  
  // Dropdowns
  const [showFromPicker, setShowFromPicker] = useState(false);
  const [showToPicker, setShowToPicker] = useState(false);
  
  // State
  const [isQuoting, setIsQuoting] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [quote, setQuote] = useState(null);
  const [liveRates, setLiveRates] = useState(CRYPTO_PRICES_USD);
  
  // Balance states
  const [bankBalance, setBankBalance] = useState({ EUR: 0, GBP: 0, USD: 0 });
  const [cryptoBalances, setCryptoBalances] = useState({});
  const [loadingBalances, setLoadingBalances] = useState(false);

  // Theme colors
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-white/60' : 'text-gray-600';
  const cardBg = isDark ? 'bg-[#111111]' : 'bg-white';
  const cardBorder = isDark ? 'border-white/10' : 'border-gray-200';
  const inputBg = isDark ? 'bg-black/40' : 'bg-gray-50';

  // Fee structure
  const FIAT_TO_CRYPTO_FEE = 0.0235; // 2.35%
  const CRYPTO_TO_CRYPTO_FEE = 0.005; // 0.5%

  const currentFee = swapMode === 'fiat-crypto' ? FIAT_TO_CRYPTO_FEE : CRYPTO_TO_CRYPTO_FEE;
  const feePercent = (currentFee * 100).toFixed(2);

  // Get auth header - memoized for stable reference
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Fetch balances on load and when user/mode changes
  useEffect(() => {
    if (user) {
      fetchBalances();
    }
  }, [user, swapMode]);

  // Fetch live crypto prices
  useEffect(() => {
    fetchLivePrices();
    const interval = setInterval(fetchLivePrices, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchBalances = async () => {
    setLoadingBalances(true);
    try {
      // Fetch bank balance for fiat
      const bankResponse = await axios.get(`${API}/banking/account?email=${user?.email}`, {
        headers: getAuthHeader()
      });
      if (bankResponse.data) {
        // Mock multi-currency balances based on account balance
        const totalBalance = bankResponse.data.balance || 0;
        setBankBalance({
          EUR: totalBalance,
          GBP: totalBalance * 0.85,
          USD: totalBalance * 1.08,
          CHF: totalBalance * 0.95,
          CAD: totalBalance * 1.47,
          AUD: totalBalance * 1.65,
          JPY: totalBalance * 162.50
        });
      }

      // Fetch crypto wallet balances
      const walletsResponse = await axios.get(`${API}/wallets/list?email=${user?.email}`, {
        headers: getAuthHeader()
      });
      if (walletsResponse.data?.wallets) {
        const balances = {};
        walletsResponse.data.wallets.forEach(wallet => {
          // Map network to crypto code
          const networkToCrypto = {
            'ethereum': 'ETH',
            'bitcoin': 'BTC',
            'solana': 'SOL',
            'bsc': 'BNB',
            'polygon': 'MATIC',
          };
          const cryptoCode = networkToCrypto[wallet.network?.toLowerCase()] || wallet.network?.toUpperCase();
          balances[cryptoCode] = wallet.balance || 0;
        });
        setCryptoBalances(balances);
      }
    } catch (error) {
      console.error('Error fetching balances:', error);
      // Set demo balances
      setBankBalance({ EUR: 5000, GBP: 4250, USD: 5400, CHF: 4750, CAD: 7350, AUD: 8250, JPY: 812500 });
      setCryptoBalances({ BTC: 0.15, ETH: 2.5, SOL: 25, BNB: 5, MATIC: 1000, USDT: 500, USDC: 500 });
    } finally {
      setLoadingBalances(false);
    }
  };

  const fetchLivePrices = async () => {
    try {
      const response = await axios.get(`${API}/trading/prices/latest`);
      if (response.data.prices) {
        const prices = {};
        response.data.prices.forEach(p => {
          prices[p.symbol?.toUpperCase()] = p.price;
        });
        setLiveRates(prev => ({ ...prev, ...prices }));
      }
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  };

  // Calculate conversion
  const calculateConversion = useCallback(() => {
    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      setToAmount('');
      setQuote(null);
      return;
    }

    setIsQuoting(true);
    const amount = parseFloat(fromAmount);
    let rate = 0;
    let outputAmount = 0;
    let feeAmount = 0;
    let effectiveRate = 0;

    if (swapMode === 'fiat-crypto') {
      // Fiat to Crypto
      const cryptoPrice = liveRates[toCurrency.code] || CRYPTO_PRICES_USD[toCurrency.code];
      
      // Convert fiat to USD first if needed
      let amountInUSD = amount;
      if (fromCurrency.code !== 'USD') {
        const xeRate = XE_RATES[fromCurrency.code]?.USD || 1;
        amountInUSD = amount * xeRate;
      }
      
      // Calculate crypto amount before fee
      const grossCrypto = amountInUSD / cryptoPrice;
      
      // Apply 2.35% fee
      feeAmount = grossCrypto * FIAT_TO_CRYPTO_FEE;
      outputAmount = grossCrypto - feeAmount;
      rate = cryptoPrice;
      effectiveRate = amount / outputAmount;
      
    } else if (swapMode === 'crypto-crypto') {
      // Crypto to Crypto
      const fromPrice = liveRates[fromCurrency.code] || CRYPTO_PRICES_USD[fromCurrency.code];
      const toPrice = liveRates[toCurrency.code] || CRYPTO_PRICES_USD[toCurrency.code];
      
      // Convert through USD
      const valueInUSD = amount * fromPrice;
      const grossCrypto = valueInUSD / toPrice;
      
      // Apply 0.5% fee
      feeAmount = grossCrypto * CRYPTO_TO_CRYPTO_FEE;
      outputAmount = grossCrypto - feeAmount;
      rate = fromPrice / toPrice;
      effectiveRate = rate * (1 - CRYPTO_TO_CRYPTO_FEE);
      
    } else {
      // Fiat to Fiat (XE rate)
      const xeRate = XE_RATES[fromCurrency.code]?.[toCurrency.code] || 1;
      const grossAmount = amount * xeRate;
      
      // Apply 0.5% fee for currency exchange
      feeAmount = grossAmount * CRYPTO_TO_CRYPTO_FEE;
      outputAmount = grossAmount - feeAmount;
      rate = xeRate;
      effectiveRate = xeRate * (1 - CRYPTO_TO_CRYPTO_FEE);
    }

    setToAmount(outputAmount.toFixed(outputAmount < 1 ? 8 : 2));
    setQuote({
      rate,
      effectiveRate,
      feeAmount,
      feePercent: currentFee * 100,
      fromAmount: amount,
      toAmount: outputAmount,
      fromCurrency: fromCurrency.code,
      toCurrency: toCurrency.code,
      valueUSD: swapMode === 'fiat-crypto' 
        ? (fromCurrency.code === 'USD' ? amount : amount * (XE_RATES[fromCurrency.code]?.USD || 1))
        : amount * (liveRates[fromCurrency.code] || 0)
    });

    setIsQuoting(false);
  }, [fromAmount, fromCurrency, toCurrency, swapMode, liveRates, currentFee]);

  // Debounced calculation
  useEffect(() => {
    const timeout = setTimeout(calculateConversion, 300);
    return () => clearTimeout(timeout);
  }, [calculateConversion]);

  // Switch mode
  const handleModeSwitch = (mode) => {
    setSwapMode(mode);
    setFromAmount('');
    setToAmount('');
    setQuote(null);
    
    if (mode === 'fiat-crypto') {
      setFromCurrency(FIAT_CURRENCIES[2]); // USD
      setToCurrency(CRYPTO_CURRENCIES[0]); // BTC
    } else {
      setFromCurrency(CRYPTO_CURRENCIES[0]); // BTC
      setToCurrency(CRYPTO_CURRENCIES[1]); // ETH
    }
  };

  // Swap direction
  const handleSwapDirection = () => {
    const tempCurrency = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(tempCurrency);
    setFromAmount(toAmount);
    setToAmount(fromAmount);
  };

  // Execute swap
  const handleSwapExecute = async () => {
    if (!user) {
      toast.error('Please log in to swap');
      return;
    }

    if (!quote || !fromAmount) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsSwapping(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/trading/swap`, {
        swap_mode: swapMode,
        from_currency: fromCurrency.code,
        to_currency: toCurrency.code,
        from_amount: parseFloat(fromAmount),
        to_amount: parseFloat(toAmount),
        rate: quote.rate,
        fee_percent: quote.feePercent
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      // Navigate to confirmation page with swap details
      navigate('/swap/confirmation', {
        state: {
          swapDetails: {
            success: true,
            transactionId: response.data?.swap?.id || `TXN-${Date.now()}`,
            swapMode: swapMode,
            fromCurrency: fromCurrency.code,
            toCurrency: toCurrency.code,
            fromAmount: parseFloat(fromAmount),
            toAmount: parseFloat(toAmount),
            rate: quote.rate,
            feePercent: quote.feePercent,
            feeAmount: quote.feeAmount,
            timestamp: new Date().toISOString(),
            estimatedValue: quote.valueUSD
          }
        }
      });
      
      // Reset form
      setFromAmount('');
      setToAmount('');
      setQuote(null);
    } catch (error) {
      console.error('Swap error:', error);
      toast.error('Swap failed. Please try again.');
    } finally {
      setIsSwapping(false);
    }
  };

  // Get available currencies for picker
  const getFromCurrencies = () => {
    if (swapMode === 'fiat-crypto') return FIAT_CURRENCIES;
    return CRYPTO_CURRENCIES;
  };

  const getToCurrencies = () => {
    if (swapMode === 'fiat-crypto') return CRYPTO_CURRENCIES;
    return CRYPTO_CURRENCIES.filter(c => c.code !== fromCurrency.code);
  };

  // Currency Picker Component
  const CurrencyPicker = ({ currencies, selected, onSelect, onClose, isCrypto = false }) => (
    <div className={`absolute top-full left-0 right-0 mt-1 ${cardBg} border ${cardBorder} rounded-xl shadow-2xl z-50 max-h-64 overflow-y-auto`}>
      {currencies.map((currency) => {
        const balance = isCrypto 
          ? cryptoBalances[currency.code] || 0
          : bankBalance[currency.code] || 0;
        return (
          <button
            key={currency.code}
            onClick={() => {
              onSelect(currency);
              onClose();
            }}
            className={`w-full flex items-center gap-3 p-3 ${
              selected.code === currency.code
                ? 'bg-purple-500/10'
                : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
            } transition-colors`}
          >
            {isCrypto ? (
              <span className="text-xl">{currency.icon}</span>
            ) : (
              <span className="text-xl">{currency.flag}</span>
            )}
            <div className="flex-1 text-left">
              <div className={`font-medium ${textPrimary}`}>{currency.code}</div>
              <div className={`text-xs ${textMuted}`}>{currency.name}</div>
            </div>
            <div className="text-right">
              {isCrypto && liveRates[currency.code] && (
                <div className={`text-xs ${textMuted} font-mono`}>
                  ${liveRates[currency.code]?.toLocaleString()}
                </div>
              )}
              <div className={`text-xs font-mono ${balance > 0 ? 'text-green-500' : textMuted}`}>
                {isCrypto ? (
                  <>{balance.toLocaleString(undefined, { maximumFractionDigits: 6 })}</>
                ) : (
                  <>{currency.symbol}{balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</>
                )}
              </div>
            </div>
            {selected.code === currency.code && (
              <Check className="w-4 h-4 text-purple-500" />
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <div data-testid="swap-page" className="min-h-screen py-6 px-3 md:px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-5">
          <h1 className={`text-2xl md:text-3xl font-bold ${textPrimary} mb-1`}>Exchange</h1>
          <p className={`${textMuted} text-sm`}>OTC Trading with Live XE Rates</p>
        </div>

        {/* Mode Tabs */}
        <div className={`flex gap-2 p-1 ${cardBg} border ${cardBorder} rounded-xl mb-4`}>
          <button
            onClick={() => handleModeSwitch('fiat-crypto')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all ${
              swapMode === 'fiat-crypto'
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                : isDark ? 'text-white/60 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <DollarSign className="w-4 h-4" />
            Fiat to Crypto
            <span className={`text-xs px-1.5 py-0.5 rounded ${swapMode === 'fiat-crypto' ? 'bg-white/20' : isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
              2.35%
            </span>
          </button>
          <button
            onClick={() => handleModeSwitch('crypto-crypto')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all ${
              swapMode === 'crypto-crypto'
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                : isDark ? 'text-white/60 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Coins className="w-4 h-4" />
            Crypto to Crypto
            <span className={`text-xs px-1.5 py-0.5 rounded ${swapMode === 'crypto-crypto' ? 'bg-white/20' : isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
              0.5%
            </span>
          </button>
        </div>

        {/* Main Swap Card */}
        <div className={`${cardBg} border ${cardBorder} rounded-2xl p-5 shadow-lg`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-500" />
              <h2 className={`font-semibold ${textPrimary}`}>
                {swapMode === 'fiat-crypto' ? 'Buy Crypto' : 'Swap Crypto'}
              </h2>
            </div>
            <button 
              onClick={fetchLivePrices}
              className={`flex items-center gap-1 text-xs ${textMuted} hover:text-purple-500 transition-colors`}
            >
              <RefreshCw className={`w-3 h-3 ${isQuoting ? 'animate-spin' : ''}`} />
              Refresh rates
            </button>
          </div>

          <div className="space-y-2">
            {/* From Input */}
            <div className={`${inputBg} border ${cardBorder} rounded-xl p-4`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs ${textMuted}`}>You pay</span>
                <div className="flex items-center gap-2">
                  {/* Balance Display */}
                  <span className={`text-xs ${textMuted} flex items-center gap-1`}>
                    <Wallet className="w-3 h-3" />
                    Balance: 
                    {loadingBalances ? (
                      <Loader2 className="w-3 h-3 animate-spin ml-1" />
                    ) : swapMode === 'fiat-crypto' ? (
                      <span className="font-mono font-medium text-green-500">
                        {fromCurrency.symbol}{(bankBalance[fromCurrency.code] || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </span>
                    ) : (
                      <span className="font-mono font-medium text-green-500">
                        {(cryptoBalances[fromCurrency.code] || 0).toLocaleString(undefined, { maximumFractionDigits: 6 })} {fromCurrency.code}
                      </span>
                    )}
                  </span>
                  <span className={`text-xs ${textMuted}`}>| Fee: {feePercent}%</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  value={fromAmount}
                  onChange={(e) => setFromAmount(e.target.value)}
                  placeholder="0.00"
                  className={`flex-1 text-2xl font-mono ${textPrimary} bg-transparent outline-none placeholder:${textMuted}`}
                />
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowFromPicker(!showFromPicker);
                      setShowToPicker(false);
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-white/10 hover:bg-white/15' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                  >
                    {swapMode === 'fiat-crypto' ? (
                      <span className="text-lg">{fromCurrency.flag}</span>
                    ) : (
                      <span className="text-lg">{fromCurrency.icon}</span>
                    )}
                    <span className={`font-medium ${textPrimary}`}>{fromCurrency.code}</span>
                    <ChevronDown className={`w-4 h-4 ${textMuted}`} />
                  </button>
                  {showFromPicker && (
                    <CurrencyPicker
                      currencies={getFromCurrencies()}
                      selected={fromCurrency}
                      onSelect={setFromCurrency}
                      onClose={() => setShowFromPicker(false)}
                      isCrypto={swapMode === 'crypto-crypto'}
                    />
                  )}
                </div>
              </div>
              {/* Max Button */}
              {user && (
                <div className="flex justify-end mt-2">
                  <button
                    onClick={() => {
                      const maxBalance = swapMode === 'fiat-crypto' 
                        ? bankBalance[fromCurrency.code] || 0
                        : cryptoBalances[fromCurrency.code] || 0;
                      setFromAmount(maxBalance.toString());
                    }}
                    className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30' : 'bg-purple-100 text-purple-600 hover:bg-purple-200'} transition-colors`}
                  >
                    MAX
                  </button>
                </div>
              )}
            </div>

            {/* Swap Direction Button */}
            <div className="flex justify-center -my-1 relative z-10">
              <button
                onClick={handleSwapDirection}
                className={`${isDark ? 'bg-black/60 border-white/20 hover:border-purple-500/50' : 'bg-white border-gray-200 hover:border-purple-500/50'} border rounded-xl p-2.5 transition-all hover:scale-110 shadow-lg`}
              >
                <ArrowDownUp className={`w-5 h-5 ${textPrimary}`} />
              </button>
            </div>

            {/* To Input */}
            <div className={`${inputBg} border ${cardBorder} rounded-xl p-4`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs ${textMuted}`}>You receive</span>
                {isQuoting && (
                  <span className="flex items-center gap-1 text-xs text-purple-500">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Calculating...
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={isQuoting ? '...' : toAmount}
                  readOnly
                  placeholder="0.00"
                  className={`flex-1 text-2xl font-mono ${textPrimary} bg-transparent outline-none`}
                />
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowToPicker(!showToPicker);
                      setShowFromPicker(false);
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-white/10 hover:bg-white/15' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                  >
                    <span className="text-lg">{toCurrency.icon || toCurrency.flag}</span>
                    <span className={`font-medium ${textPrimary}`}>{toCurrency.code}</span>
                    <ChevronDown className={`w-4 h-4 ${textMuted}`} />
                  </button>
                  {showToPicker && (
                    <CurrencyPicker
                      currencies={getToCurrencies()}
                      selected={toCurrency}
                      onSelect={setToCurrency}
                      onClose={() => setShowToPicker(false)}
                      isCrypto={true}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quote Details */}
          {quote && (
            <div className={`mt-4 p-4 ${isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-purple-50 border-purple-200'} border rounded-xl`}>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className={textMuted}>Exchange Rate</span>
                  <span className={textPrimary}>
                    1 {fromCurrency.code} = {quote.rate < 1 ? quote.rate.toFixed(8) : quote.rate.toFixed(4)} {toCurrency.code}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={textMuted}>Fee ({quote.feePercent}%)</span>
                  <span className={textPrimary}>
                    {quote.feeAmount < 0.0001 ? quote.feeAmount.toFixed(8) : quote.feeAmount.toFixed(6)} {toCurrency.code}
                  </span>
                </div>
                <div className={`flex justify-between pt-2 border-t ${cardBorder}`}>
                  <span className={`font-medium ${textPrimary}`}>You receive</span>
                  <span className={`font-bold text-purple-500`}>
                    {quote.toAmount < 1 ? quote.toAmount.toFixed(8) : quote.toAmount.toFixed(4)} {toCurrency.code}
                  </span>
                </div>
                {quote.valueUSD > 0 && (
                  <div className="flex justify-between">
                    <span className={textMuted}>Value</span>
                    <span className={textMuted}>≈ ${quote.valueUSD.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className={`mt-4 p-3 ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'} border rounded-xl flex items-start gap-2`}>
            <Info className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'} flex-shrink-0 mt-0.5`} />
            <div className={`text-xs ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              {swapMode === 'fiat-crypto' ? (
                <>Rates powered by XE and CoinGecko. A {feePercent}% service fee is applied to all fiat-to-crypto conversions.</>
              ) : (
                <>Crypto-to-crypto swaps use live market rates with a {feePercent}% trading fee. Best execution guaranteed.</>
              )}
            </div>
          </div>

          {/* Execute Button */}
          <button
            onClick={handleSwapExecute}
            disabled={isSwapping || !quote || !user}
            className="w-full mt-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-4 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 shadow-lg"
          >
            {!user ? (
              'Connect Wallet'
            ) : isSwapping ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : !quote ? (
              'Enter Amount'
            ) : (
              <>
                {swapMode === 'fiat-crypto' ? 'Buy' : 'Swap'} {toCurrency.code}
              </>
            )}
          </button>
        </div>

        {/* Live Prices Ticker */}
        <div className={`mt-4 ${cardBg} border ${cardBorder} rounded-xl p-4`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-sm font-medium ${textPrimary}`}>Live Prices</span>
            <span className={`text-xs ${textMuted}`}>Updated every 30s</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {['BTC', 'ETH', 'SOL', 'BNB'].map(code => {
              const crypto = CRYPTO_CURRENCIES.find(c => c.code === code);
              const price = liveRates[code] || CRYPTO_PRICES_USD[code];
              return (
                <div 
                  key={code}
                  className={`text-center p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}
                >
                  <span className="text-lg">{crypto?.icon}</span>
                  <div className={`text-xs font-medium ${textPrimary}`}>{code}</div>
                  <div className={`text-xs ${textMuted} font-mono`}>
                    ${price?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 text-center space-y-1">
          <p className={`text-xs ${textMuted}`}>
            OTC Trading • XE Exchange Rates • CoinGecko Prices
          </p>
          <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
            Fiat → Crypto: 2.35% • Crypto → Crypto: 0.5%
          </p>
        </div>
      </div>
    </div>
  );
};

export default Swap;
